import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Heart, ShoppingCart, RefreshCw } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Logo } from "@/components/Logo";
import { MarketBanner } from "@/components/MarketBanner";
import { ProductCard } from "@/components/ProductCard";
import { BottomNav } from "@/components/BottomNav";
import { ProductImage } from "@/components/ProductImage";

export default function Market() {
  const { user } = useAuth();
  const { count, addItem } = useCart();
  const navigate = useNavigate();

  const [market, setMarket] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [trending, setTrending] = useState([]);
  const [frequent, setFrequent] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);

  const loadStatic = useCallback(() => {
    api.get("/market-day").then((r) => setMarket(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products/trending").then((r) => setTrending(r.data)).catch(() => {});
    if (user) {
      api.get("/orders/frequent").then((r) => setFrequent(r.data)).catch(() => {});
      api.get("/favourites").then((r) => setFavIds(r.data.map((p) => p.product_id))).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    loadStatic();
  }, [loadStatic]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        let data;
        if (cat === "❤️ Favourites") {
          if (!user) {
            if (alive) setProducts([]);
            return;
          }
          data = (await api.get("/favourites")).data;
        } else {
          data = (await api.get("/products", { params: { category: cat === "All" ? "" : cat, search } })).data;
        }
        if (alive) setProducts(data);
      } catch {
        if (alive) setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, search, user?.user_id, reloadKey]);

  const toggleFav = async (product) => {
    if (!user) {
      toast.info("Sign in to save favourites");
      navigate("/login");
      return;
    }
    try {
      const { data } = await api.post(`/favourites/${product.product_id}/toggle`);
      setFavIds(data.favourites);
      toast.success(data.active ? "Added to favourites ❤️" : "Removed from favourites");
      if (cat === "❤️ Favourites") setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    loadStatic();
    setReloadKey((k) => k + 1);
    setTimeout(() => setRefreshing(false), 600);
    toast.success("Refreshed");
  };

  const addAllFavourites = () => {
    const avail = products.filter((p) => p.available);
    if (avail.length === 0) {
      toast.info("No available favourites to add");
      return;
    }
    avail.forEach((p) => addItem(p));
    toast.success(`Added ${avail.length} favourite(s) to your basket`);
    navigate("/cart");
  };

  const trendingTop3 = trending.slice(0, 3).map((p) => p.product_id);
  const chips = ["All", ...categories, "❤️ Favourites"];

  const groups = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      const key = `${p.category}|${p.name}`;
      if (!map.has(key)) map.set(key, { key, name: p.name, variants: [] });
      map.get(key).variants.push(p);
    }
    return Array.from(map.values());
  }, [products]);

  return (
    <div className="max-w-md mx-auto bg-[#F5F8F6] min-h-screen pb-24">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[#F5F8F6] px-4 py-3 pt-safe">
        <div className="flex items-center justify-between">
          <Logo size={38} />
          <button onClick={() => navigate("/cart")} className="relative w-11 h-11 rounded-full bg-[#DDF0E5] flex items-center justify-center active:scale-90 transition-transform" data-testid="header-cart">
            <ShoppingCart size={20} className="text-[#0B7A3B]" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#C0392B] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{count}</span>
            )}
          </button>
        </div>
      </header>

      <div className="px-4 pt-4 space-y-4">
        <MarketBanner market={market} />

        {/* Search */}
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B8375]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search catfish, yam, rice…"
            data-testid="search-input"
            className="w-full bg-white rounded-full pl-11 pr-4 py-3 text-[#122117] placeholder-[#6B8375] focus:outline-none focus:ring-2 focus:ring-[#0B7A3B] border border-[#F5F8F6]"
          />
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="category-row">
          {chips.map((c) => {
            const active = cat === c;
            const isFav = c === "❤️ Favourites";
            return (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`chip-${c}`}
                className={`h-9 shrink-0 rounded-full px-4 text-sm font-semibold flex items-center gap-1 transition-colors ${
                  active ? (isFav ? "bg-[#C0392B] text-white" : "bg-[#0B7A3B] text-white") : "bg-white text-[#122117] border border-[#F5F8F6]"
                }`}
              >
                {isFav && <Heart size={14} className={active ? "fill-white" : "fill-[#C0392B] text-[#C0392B]"} />}
                {isFav ? "Favourites" : c}
              </button>
            );
          })}
        </div>
      </div>

      {/* Buy again */}
      {frequent.length > 0 && cat === "All" && !search && (
        <Strip title="Buy again" subtitle="Your most-ordered" items={frequent} onAdd={addItem} testid="buy-again" />
      )}
      {/* Trending */}
      {trending.length > 0 && cat === "All" && !search && (
        <Strip title="Trending this market" subtitle="What others order most" items={trending} onAdd={addItem} testid="trending" />
      )}

      {/* Grid */}
      <div className="px-4 mt-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-extrabold text-[#122117]">{cat === "All" ? "All products" : cat.replace("❤️ ", "")}</h2>
          <button onClick={refresh} className="flex items-center gap-1 text-xs font-semibold text-[#6B8375]" data-testid="refresh-btn">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
        {cat === "❤️ Favourites" && products.length > 0 && (
          <button
            onClick={addAllFavourites}
            data-testid="add-all-favourites"
            className="w-full mb-3 flex items-center justify-center gap-2 bg-[#0B7A3B] text-white font-bold rounded-full py-3 active:scale-95 transition-transform hover:bg-[#095F2E]"
          >
            <Heart size={16} className="fill-white" /> Add all favourites to basket
          </button>
        )}
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-[16px] p-2.5 animate-pulse">
                <div className="aspect-square rounded-[12px] bg-[#F5F8F6]" />
                <div className="h-3 bg-[#F5F8F6] rounded mt-2" />
                <div className="h-3 bg-[#F5F8F6] rounded mt-1 w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 text-[#6B8375]" data-testid="empty-products">
            {cat === "❤️ Favourites" ? "No favourites yet. Tap the heart on products you love." : "No products found."}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {groups.map((g, i) => (
              <ProductCard
                key={g.key}
                variants={g.variants}
                index={i}
                popularIds={trendingTop3}
                favIds={favIds}
                onToggleFav={toggleFav}
              />
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

function Strip({ title, subtitle, items, onAdd, testid }) {
  return (
    <div className="mt-5" data-testid={`strip-${testid}`}>
      <div className="px-4 flex items-baseline justify-between">
        <h2 className="text-lg font-extrabold text-[#122117]">{title}</h2>
        <span className="text-xs text-[#6B8375]">{subtitle}</span>
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar px-4 mt-2 pb-1">
        {items.map((p) => (
          <div key={p.product_id} className="shrink-0 w-32 bg-white rounded-[14px] p-2 border border-[#F5F8F6]">
            <div className="aspect-square rounded-[10px] overflow-hidden bg-[#F5F8F6]">
              <ProductImage src={p.image} alt={p.name} className="w-full h-full object-cover" iconSize={20} />
            </div>
            <p className="text-[12px] font-bold text-[#122117] line-clamp-2 mt-1.5 min-h-[32px]">{p.name}</p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm font-extrabold text-[#0B7A3B]">£{p.price.toFixed(2)}</span>
              <button
                onClick={() => { onAdd(p); toast.success(`${p.name} added`); }}
                className="w-7 h-7 rounded-full bg-[#0B7A3B] text-white flex items-center justify-center active:scale-90 transition-transform"
                data-testid={`quickadd-${p.product_id}`}
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
